import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Skeleton from "@mui/material/Skeleton";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import DashboardLayout from "../layouts/dashboard";
import { StaggerContainer, StaggerItem } from "../../interface/shared/animations";
import { useGoodsInDetail } from "../../api/goodsin";
import { ExpiresAtDisplay, ShelfLifeDisplay } from "../../interface/shared/expirydisplay";
import { WeightDisplay } from "../../interface/shared/weightdisplay";
import { useApiService } from "../../shared/apiservice";
import { Attachment, User } from "../../shared/types";
import { PrintSharp } from "@mui/icons-material";

export default function GoodsInDetails() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { delivery, errors, loading, fetchDelivery } = useGoodsInDetail(id);
    const apiService = useApiService();
    const [printingPalletId, setPrintingPalletId] = useState<number | string | null>(null);

    const printPalletLabel = async (pallet: any) => {
        setPrintingPalletId(pallet.id);
        try {
            const response = await apiService.get(`/pallets/${pallet.id}/label`);
            if (!response.ok) {
                window.alert("Failed to retrieve pallet label. Please try again.");
                return;
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            // Create a hidden iframe, load the PDF and trigger print
            const iframe = document.createElement("iframe");
            iframe.style.display = "none";
            iframe.src = url;
            document.body.appendChild(iframe);

            iframe.onload = () => {
                try {
                    iframe.contentWindow?.focus();
                    iframe.contentWindow?.print();
                } finally {
                    // Cleanup after a short delay to give the print dialog time to open
                    setTimeout(() => {
                        URL.revokeObjectURL(url);
                        if (document.body.contains(iframe)) document.body.removeChild(iframe);
                    }, 1000);
                }
            };
        } catch (err) {
            window.alert("Error printing pallet label. Please try again.");
        } finally {
            setPrintingPalletId(null);
        }
    };

    return (
        <DashboardLayout>
            <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                    <Button
                        startIcon={<ArrowBackRoundedIcon />}
                        onClick={() => navigate("/goods-in")}
                    >
                        Back
                    </Button>
                </Stack>

                {errors.length > 0 && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        action={
                            <Button color="inherit" size="small" onClick={fetchDelivery}>
                                Retry
                            </Button>
                        }
                    >
                        {errors.join(" ")}
                    </Alert>
                )}

                {loading ? <_DetailsSkeleton /> : delivery && (
                    <StaggerContainer>
                        <StaggerItem>
                            <_DeliverySummary
                                reference={delivery.reference}
                                vrn={delivery.vrn}
                                temp={delivery.temp}
                                arrivedAt={delivery.arrived_at}
                                startedAt={delivery.started_at}
                                completedAt={delivery.completed_at}
                                attachmentCount={delivery.attachments.length}
                                palletCount={delivery.pallets.length}
                                user={delivery.user}
                            />
                        </StaggerItem>

                        <StaggerItem>
                            <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
                                Pallets ({delivery.pallets.length})
                            </Typography>


                            {delivery.pallets.map((pallet, idx) => (
                                <Card variant="outlined" sx={{ mt: 4, mb: 2 }}>
                                    <CardContent>

                                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 3 }}>
                                            <Typography variant="h6" fontWeight={600} sx={{ mt: 1, mb: 2 }}>
                                                Pallet {idx + 1}
                                            </Typography>

                                            <Button
                                                variant="outlined"
                                                startIcon={printingPalletId === pallet.id ? <CircularProgress size={24} /> : <PrintSharp />}
                                                onClick={() => printPalletLabel(pallet)}
                                                disabled={printingPalletId === pallet.id}
                                            >
                                                Print Label
                                            </Button>
                                        </Stack>

                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Code</TableCell>
                                                    <TableCell>Product</TableCell>
                                                    <TableCell>Quantity</TableCell>
                                                    <TableCell>Weight</TableCell>
                                                    <TableCell>Shelf Life</TableCell>
                                                    <TableCell>Expires</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {pallet.items.map((item) => (
                                                    <TableRow key={item.id}>
                                                        <TableCell>{item.product.code}</TableCell>
                                                        <TableCell>{item.product.name}</TableCell>
                                                        <TableCell>{item.quantity > 0 ? `${item.quantity}` : '-'}</TableCell>
                                                        <TableCell><WeightDisplay value={item.weight} /></TableCell>
                                                        <TableCell>
                                                            <ShelfLifeDisplay value={item.shelf_life} variant="simple" />
                                                        </TableCell>
                                                        <TableCell>
                                                            <ExpiresAtDisplay value={item.expires_at} />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>

                                    </CardContent>

                                </Card>
                            ))}
                        </StaggerItem>

                        {delivery.attachments.length > 0 && (
                            <StaggerItem>
                                <_AttachmentsSection attachments={delivery.attachments} />
                            </StaggerItem>
                        )}
                    </StaggerContainer>
                )}
            </Box>
        </DashboardLayout>
    );
}

function _DeliverySummary({
    reference,
    vrn,
    temp,
    arrivedAt,
    startedAt,
    completedAt,
    user,
    attachmentCount,
    palletCount,
}: {
    reference: string;
    vrn: string;
    temp: number;
    arrivedAt: Date;
    startedAt: Date;
    completedAt: Date;
    user?: User;
    attachmentCount: number;
    palletCount: number;
}) {
    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="h6">
                            {reference}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            VRN: {vrn}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Chip label={`${palletCount} pallets`} size="small" />
                        <Chip
                            label={`${attachmentCount} attachments`}
                            size="small"
                            color={attachmentCount > 0 ? "success" : "error"}
                        />
                    </Stack>
                </Stack>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Temperature</Typography>
                        <Typography variant="body1" fontWeight={500}>{temp}°C</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Arrived</Typography>
                        <Typography variant="body1" fontWeight={500}>{arrivedAt.toLocaleDateString()}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Started</Typography>
                        <Typography variant="body1" fontWeight={500}>{startedAt.toLocaleDateString()}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">Completed</Typography>
                        <Typography variant="body1" fontWeight={500}>{completedAt.toLocaleDateString()}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 2 }}>
                        <Typography variant="caption" color="text.secondary">User</Typography>
                        <Typography variant="body1" fontWeight={500}>{user?.name ?? '-'}</Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

function _AttachmentsSection({ attachments }: { attachments: Attachment[] }) {
    const [open, setOpen] = useState(false);

    return (
        <>
            <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
                Attachments ({attachments.length})
            </Typography>
            <_AttachmentsList attachments={attachments} />
        </>
    );
}

function _AttachmentsList({ attachments }: { attachments: Attachment[] }) {
    const apiService = useApiService();
    const [loadingKey, setLoadingKey] = useState<string | null>(null);
    const [snackMessage, setSnackMessage] = useState<string | null>(null);

    async function handleDownload(attachment: Attachment) {
        setLoadingKey(attachment.key);
        try {
            const response = await apiService.get(`/attachments/${attachment.key}`);
            if (!response.ok) {
                setSnackMessage("Failed to get download link. Please try again.");
                return;
            }
            const data: Attachment = await response.json();
            if (data.status === "complete" && data.url) {
                window.open(data.url, "_blank");
            } else {
                setSnackMessage(`File "${attachment.filename}" is unavailable. It may still be processing.`);
            }
        } catch {
            setSnackMessage("Failed to get download link. Please try again.");
        } finally {
            setLoadingKey(null);
        }
    }

    return (
        <>
            <Card variant="outlined">
                <List disablePadding>
                    {attachments.map((attachment) => (
                        <ListItem
                            key={attachment.key}
                            divider
                            secondaryAction={
                                <Button
                                    size="small"
                                    startIcon={
                                        loadingKey === attachment.key
                                            ? <CircularProgress size={16} />
                                            : <FileDownloadRoundedIcon fontSize="small" />
                                    }
                                    disabled={loadingKey === attachment.key}
                                    onClick={() => handleDownload(attachment)}
                                >
                                    Download
                                </Button>
                            }
                        >
                            <ListItemIcon sx={{ mr: 1 }}>
                                <InsertDriveFileRoundedIcon sx={{ fontSize: 32 }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={attachment.filename}
                                secondary={attachment.mime_type}
                            />
                        </ListItem>
                    ))}
                </List>
            </Card>
            <Snackbar
                open={snackMessage !== null}
                autoHideDuration={4000}
                onClose={() => setSnackMessage(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="warning" onClose={() => setSnackMessage(null)} variant="filled">
                    {snackMessage}
                </Alert>
            </Snackbar>
        </>
    );
}

function _DetailsSkeleton() {
    return (
        <>
            <Card variant="outlined">
                <CardContent>
                    <Skeleton variant="text" width={200} height={32} />
                    <Skeleton variant="text" width={120} height={20} sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Grid key={i} size={{ xs: 6, sm: 3 }}>
                                <Skeleton variant="text" width={80} height={16} />
                                <Skeleton variant="text" width={100} height={24} />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
            <Skeleton variant="text" width={120} height={28} sx={{ mt: 4, mb: 2 }} />
            <Card variant="outlined">
                <CardContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Code</TableCell>
                                <TableCell>Product</TableCell>
                                <TableCell>Quantity</TableCell>
                                <TableCell>Weight</TableCell>
                                <TableCell>Shelf Life</TableCell>
                                <TableCell>Expires</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.from({ length: 3 }).map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </>
    );
}
